import { LoadingRow } from '@folio/components/module-elements/LoadingRow';
import { formatDateWithTime } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import {
  type NotificationReason,
  notificationReasonEnum,
  configControllerGetBusinessRolePermissionsQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
  notificationControllerListNotificationsQueryOptions,
} from 'korint-sdk';
import { useFeatureFlag } from 'korint-sdk/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PaginationComponent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui';
import { NotificationTypeBadge } from './NotificationTypeBadge';
import { NotificationsStatusBadge } from './NotificationsStatusBadge';
import { RetryNotificationDialog } from './RetryNotificationDialog';
import { sentRecipient } from './sentRecipient';
import { SendNotificationDialog } from './SendNotificationDialog';
import { useSendableNotificationReasons } from './useSendableNotificationReasons';
import { Stack } from '@folio/components/ui/flex';

type NotificationsCardProps = Readonly<{
  policyId: string;
  productId: string;
}>;

// AR24 stores the recipient's open timestamp as `view_date` inside the opaque notification metadata.
const parseOpenedAt = (metadata: object | undefined): Date | undefined => {
  const viewDate = (metadata as { view_date?: unknown })?.view_date;
  if (typeof viewDate !== 'string') return undefined;
  const openedAt = new Date(viewDate);
  return Number.isNaN(openedAt.getTime()) ? undefined : openedAt;
};

const notificationReasons: readonly string[] = Object.values(notificationReasonEnum);

const isNotificationReason = (reason: string | undefined): reason is NotificationReason =>
  reason !== undefined && notificationReasons.includes(reason);

export function NotificationsCard({ policyId, productId }: NotificationsCardProps) {
  const { t } = useTranslation();
  const isNotificationsV2Enabled = useFeatureFlag('feat_notifications_v2');

  const { data: user } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const [searchParams] = useSearchParams();

  const currentPage = useMemo(() => {
    const page = Number.parseInt(searchParams.get('page') ?? '', 10);
    return Number.isNaN(page) || page < 1 ? 1 : page;
  }, [searchParams]);

  const { data: notifications, status } = useQuery({
    ...notificationControllerListNotificationsQueryOptions({
      externalId: policyId,
      count: 10,
      page: currentPage,
    }),
  });

  const { data: canSendNotification = false } = useQuery({
    ...configControllerGetBusinessRolePermissionsQueryOptions({
      businessRoleId: user?.brokerRole!,
      productId,
    }),
    enabled: !!user?.brokerRole,
    select: data => data.allowedPermissions.some(p => p.action === 'GENERATE_FORMAL_NOTICE'),
  });

  const sendableReasons = useSendableNotificationReasons(productId);

  const canSendNotificationAndPolicyIsActive = canSendNotification && sendableReasons.length > 0;

  return (
    <Card>
      <CardHeader className="flex flex-row justify-between items-center">
        <CardTitle>{t('notifications.cardTitle')}</CardTitle>
        {canSendNotificationAndPolicyIsActive && (
          <SendNotificationDialog policyId={policyId} reasons={sendableReasons} />
        )}
      </CardHeader>
      <CardContent className="pt-2">
        <Stack>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('notifications.tableHeaders.name')}</TableHead>
                {isNotificationsV2Enabled && (
                  <TableHead>{t('notifications.tableHeaders.reason')}</TableHead>
                )}
                <TableHead>{t('notifications.tableHeaders.sentAt')}</TableHead>
                <TableHead>{t('notifications.tableHeaders.openedAt')}</TableHead>
                <TableHead>{t('notifications.tableHeaders.status')}</TableHead>
                {isNotificationsV2Enabled && canSendNotificationAndPolicyIsActive && (
                  <TableHead>{t('notifications.tableHeaders.actions')}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {status === 'pending' ? (
                <>
                  <LoadingRow />
                  <LoadingRow />
                  <LoadingRow />
                </>
              ) : status === 'error' ? (
                <TableRow>
                  <TableCell colSpan={42} className="text-center text-muted-foreground">
                    {t('notifications.errors.fetch')}
                  </TableCell>
                </TableRow>
              ) : (
                notifications.data.map(notification => {
                  const openedAt = parseOpenedAt(notification.metadata);
                  const { reason } = notification;
                  return (
                    <TableRow key={notification.id} data-testid="notification-row">
                      <TableCell>
                        <NotificationTypeBadge type={notification.type} />
                      </TableCell>
                      {isNotificationsV2Enabled && (
                        <TableCell>
                          {notification.reason
                            ? t(`notifications.reasons.${notification.reason}`, {
                                defaultValue: notification.reason,
                              })
                            : '—'}
                        </TableCell>
                      )}
                      <TableCell>{formatDateWithTime(new Date(notification.createdAt))}</TableCell>
                      <TableCell>{openedAt ? formatDateWithTime(openedAt) : '—'}</TableCell>
                      <TableCell>
                        <NotificationsStatusBadge status={notification.status} />
                      </TableCell>
                      {isNotificationsV2Enabled && canSendNotificationAndPolicyIsActive && (
                        <TableCell>
                          {notification.type === 'ERE' && isNotificationReason(reason) && (
                            <RetryNotificationDialog
                              policyId={policyId}
                              reason={reason}
                              initialRecipient={sentRecipient(notification)}
                            />
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          <PaginationComponent
            totalData={notifications?.totalCount ?? 0}
            PAGE_SIZE={10}
            isLoading={status === 'pending'}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
